<!-- eslint-disable max-len -->
<template>
  <v-main>
    <div class="text-center">
      <v-form ref="companyForm">
        <v-dialog
          v-model="newCompanyDialog"
          width="400"
        >
          <v-card light>
            <v-card-title>Create Company</v-card-title>
            <v-card-subtitle>Add this company to an existing user</v-card-subtitle>
            <v-card-text>
              <v-text-field
                class="px-4"
                label="Name"
                readonly
                v-model="selectedUser"
              ></v-text-field>
              <v-text-field
                class="px-4"
                label="Email"
                readonly
                v-model="selectedEmail"
              ></v-text-field>
              <v-text-field
                class="px-4"
                label="Company Name"
                v-model="newCompanyName"
                :rules="[rules.required]"
                @blur="() => $refs.companyForm.resetValidation()"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                text
                @click="closeNewCompanyDialog"
              >Cancel</v-btn>
              <v-btn
                text
                color="primary"
                @click="addCompanyToUser"
              >Ok</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-form>
    </div>
    <div class="text-center">
      <v-dialog
        v-model="sendSignatureDialog"
        width="400"
      >
        <v-card light>
          <v-card-title>Send signature request</v-card-title>
          <v-card-subtitle>Generate a new {{sendSignatureType}} document and send via email</v-card-subtitle>
          <v-card-text>
            <v-text-field
              class="px-4"
              label="Email"
              persistent-placeholder
              placeholder="john.doe@example.com"
              v-model="sendSignatureEmail"
              @keyup.enter="sendSignatureRequest"
              v-bind="{
                error: !!docSignErrorMessage,
                ...(!!docSignErrorMessage && { 'error-messages': [ docSignErrorMessage ] })
              }"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="cancelRequestDialog"
            >Cancel</v-btn>
            <v-btn
              text
              color="primary"
              @click="sendSignatureRequest"
              :loading="sendSignatureLoading"
            >
              Send<v-icon x-small right>mdi-send</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <div class="text-center">
      <v-dialog
        v-model="docSignSuccessful"
        width="400"
      >
        <v-card light>
          <v-card-title>Success!</v-card-title>
          <v-card-text>The request was sent successfully!</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="success"
              @click="docSignSuccessful = false"
            >
              Ok<v-icon right>mdi-check</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-row no-gutters>
      <v-col cols="12">
        <v-sheet
          color="sheet"
          height="150"
        >
          <!--  -->
        </v-sheet>
      </v-col>
      <v-container :class="$vuetify.breakpoint.name == 'lg' ? 'px-16' : ''">
        <v-row no-gutters>
          <v-col
            cols="12"
            style="margin-top: -150px"
          >
            <v-card
              flat
              style="background-color: transparent !important;"
              max-width="80vw"
            >
              <v-card-title class="text-h4">Prospect Status</v-card-title>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            style="margin-top: -48px; position: relative;"
          >
            <v-card
              flat
              outlined
              rounded="lg"
            >
              <v-card-title>
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  clearable
                ></v-text-field>
              </v-card-title>
              <v-data-table
                :headers="headers"
                :items="prospects"
                item-key="id"
                :expanded.sync="expanded"
                show-expand
                sort-by="invitedByName"
                group-by="invitedByName"
                :search="search"
                @click:row="expandRow"
              >
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:group.header="{items, isOpen, toggle}">
                  <th colspan="12">
                    <v-icon
                      @click="toggle"
                    >
                      {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
                    </v-icon>
                    {{ items[0].invitedByName }}
                  </th>
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.fullName="{item}">
                  {{ item.firstName }} {{ item.lastName }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.userCreationDate="{item}">
                  {{ item.userCreationDate.split('T')[0] }}
                </template>
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.actions="{item}">
                  <v-tooltip bottom>
                    <template v-slot:activator="{on, attrs}">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        :loading="item.loading"
                        @click.stop="resendInvitation(item)"
                        v-show="item.verified != true"
                      >
                        <v-icon small>mdi-send</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white;">Re-send invitation</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{on, attrs}">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        @click.stop="newCompanyDialog = true; updateSelectedUser(item)"
                      >
                        <v-icon>mdi-home-plus-outline</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white;">Add a new company</span>
                  </v-tooltip>
                </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="px-0">
                  <div :width="headers.length">
                    <v-simple-table
                      dense
                    >
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th></th>
                            <th></th>
                            <th class="text-left">Name</th>
                            <th class="text-left">Prospect Status</th>
                            <th class="text-left">Send Request</th>
                            <th class="text-left">Benefit Status</th>
                            <th class="text-left">Send Request</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="company in item.userCompanies"
                            :key="company.packetGuid"
                            @click="openInfoDialog(company)"
                          >
                            <td></td>
                            <td></td>
                            <td>{{ company.companyName }}</td>
                            <td>{{ company.prospect_Status }}</td>
                            <td>
                              <v-btn
                                v-if="company.prospect_Status === 'In Progress'"
                                icon
                                x-small
                                @click.stop="sendRequestDialog('registration', company.packetGuid)"
                              >
                                <v-icon>mdi-send</v-icon>
                              </v-btn>
                            </td>
                            <td> {{ company.benefit_Status }}</td>
                            <td>
                              <v-btn
                                v-if="company.benefit_Status === 'In Progress'"
                                icon
                                x-small
                                 @click.stop="sendRequestDialog('benefits', company.packetGuid)"
                              >
                                <v-icon>mdi-send</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </td>
              </template>
              </v-data-table>
            </v-card>
          </v-col>
          <UserInfoDialog />
        </v-row>
      </v-container>
    </v-row>
  </v-main>
</template>

<script>
import UserInfoDialog from './UserInfo/UserInfoDialog.vue';

export default {
  name: 'ProspectStatus',
  components: {
    UserInfoDialog,
  },
  data: () => ({
    search: '',
    expanded: [],
    newCompanyDialog: false,
    sendSignatureEmail: null,
    sendSignatureType: null,
    sendSignaturePacketGuid: null,
    sendSignatureLoading: false,
    selectedUser: '',
    selectedEmail: '',
    selectedUserGuid: '',
    newCompanyName: null,
    rules: {
      required: (value) => !!value || 'Required',
    },
  }),
  watch: {
    sendSignatureEmail() {
      this.$store.commit('updateDocSignErrorMessage', null);
    },
  },
  computed: {
    headers() {
      return this.$store.state.datatable.headers;
    },
    prospects() {
      if (this.$store.state.prospects) {
        return this.$store.state.prospects.map((item, index) => ({
          id: index,
          ...item,
        }));
      }
      return null;
    },
    docSignErrorMessage() {
      return this.$store.state.docSignErrorMessage;
    },
    docSignSuccessful: {
      get() {
        return this.$store.state.docSignSuccessful;
      },
      set(value) {
        this.$store.commit('updateDocSignSuccessful', value);
      },
    },
    sendSignatureDialog: {
      get() {
        return this.$store.state.sendSignatureDialog;
      },
      set(value) {
        this.$store.commit('updateSendSignatureDialog', value);
      },
    },
  },
  mounted() {
    this.$store.dispatch('getAllProspects');
  },
  methods: {
    openInfoDialog(value) {
      this.$store.commit('updateUserInfoPacketGuid', value.packetGuid);
      this.$store.commit('updateSignedDocuments', []);
      this.$store.commit('updateUserDocuments', []);
      this.$store.commit('updateSignatureSkeletonLoader', true);
      this.$store.dispatch('AdminGetProspectPacket').then(() => {
        if (this.$store.state.prospectPacket.flags.hasBenefitPacket) {
          this.$store.dispatch('AdminGetBenefitsPacket');
        }
        // this.$store.dispatch('getAllUserDocuments');
        this.$store.dispatch('getAllSignedDocuments').then(() => {
          // Set skeleton loader as false
          this.$store.commit('updateSignatureSkeletonLoader', false);
        });
      });
      this.$store.commit('updateUserInfoDialog', true);
    },
    cancelRequestDialog() {
      this.sendSignatureDialog = false;
      this.$store.commit('updateDocSignErrorMessage', null);
    },
    sendRequestDialog(signatureType, packetGuid) {
      this.sendSignatureType = signatureType;
      this.sendSignaturePacketGuid = packetGuid;
      this.sendSignatureDialog = true;
    },
    sendSignatureRequest() {
      this.sendSignatureLoading = true;
      const formData = new FormData();
      formData.append('email', this.sendSignatureEmail);
      formData.append('companyPacketGuid', this.sendSignaturePacketGuid);
      formData.append('documentType', this.sendSignatureType);

      this.$store.dispatch('sendSignatureRequest', formData).then(() => {
        this.sendSignatureLoading = false;
      });
    },
    resendInvitation(item) {
      this.$store.commit('toggleAdminProspectBtnLoading', item);
      this.$store.dispatch('resendInvite', item.email).then(() => {
        this.$store.commit('toggleAdminProspectBtnLoading', item);
      });
    },
    expandRow(row, event) {
      if (event.isExpanded) {
        const expandedIndex = this.expanded.findIndex((i) => i === row);
        this.expanded.splice(expandedIndex, 1);
      } else {
        this.expanded.push(row);
      }
    },
    updateSelectedUser(user) {
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.selectedEmail = user.email;
      this.selectedUserGuid = user.userGuid;
    },
    resetSelectedUser() {
      this.selectedUserGuid = '';
      this.selectedUser = '';
      this.selectedEmail = '';
      this.newCompanyName = null;
    },
    addCompanyToUser() {
      const newCompanyInfo = {
        userGuid: this.selectedUserGuid,
        name: this.newCompanyName,
      };

      if (this.$refs.companyForm.validate()) {
        this.$store.dispatch('addCompanyToUser', newCompanyInfo).then(() => {
          this.newCompanyDialog = false;
          this.resetSelectedUser();
        });
      }
    },
    closeNewCompanyDialog() {
      this.newCompanyDialog = false;
      this.resetSelectedUser();
    },
  },
};
</script>

<style lang="scss" scoped>
.theme--dark .v-tooltip__content {
  background: black;
}
</style>
